import type { CodiceContatore } from '@praticheenergia/domain'
import type { ConsumoMensile } from './consumi'
import { aggiungiRiga, ambitoDi, type QuadriInCostruzione } from './costruzione'
import { arrotonda, ZERO, type Dec } from './dec'
import { bloccante, type Diagnostica } from './diagnostica'
import { valuta, type ContestoCalcolo } from './formula'
import { mesiDelPeriodo, type DatiPeriodo } from './ingresso'
import type { QuadroModello } from './modello'
import type { RegolePeriodo } from './regole-periodo'

/**
 * Le aliquote riferibili da `op: 'prodotto'`. Gli importi fissi (`fissoMensile`) non
 * sono aliquote e restano fuori: moltiplicare dei kWh per 4.820 non vuol dire nulla.
 */
function aliquoteScalari(regole: RegolePeriodo): Record<string, Dec> {
  return Object.fromEntries(
    Object.entries(regole.aliquote).filter(([, a]) => !(typeof a === 'object' && 'fissoMensile' in a)),
  ) as Record<string, Dec>
}

/** Le formule parlano di consumi del mese: `lettura` somma i kWh del mese per codice contatore. */
function contestoDelMese(
  consumi: readonly ConsumoMensile[],
  dati: DatiPeriodo,
  regole: RegolePeriodo,
  mese: number,
  righi: Record<string, Dec>,
): ContestoCalcolo {
  const perContatore: Partial<Record<CodiceContatore, Dec>> = {}
  const perTipologia: Record<string, Dec> = {}
  const tipologiaDi = new Map(dati.contatori.map((c) => [c.id, c.tipologiaConsumo]))
  for (const c of consumi) {
    if (c.mese !== mese) continue
    perContatore[c.codice] = (perContatore[c.codice] ?? ZERO).plus(c.kwh)
    const tipologia = tipologiaDi.get(c.contatoreId)
    if (tipologia !== undefined) {
      const chiave = `${c.codice}|${tipologia}`
      perTipologia[chiave] = (perTipologia[chiave] ?? ZERO).plus(c.kwh)
    }
  }
  return {
    consumoPerContatore: perContatore,
    consumoPerTipologia: perTipologia,
    righiCalcolati: righi,
    aliquote: aliquoteScalari(regole),
    tipologia: dati.tipologia,
  }
}

/**
 * Consumo per differenza, righi J/L/M configurati per impianto e quadro M dalle
 * letture. I righi si valutano nell'ordine dichiarato: un rigo puo' riferire
 * solo righi gia' calcolati nello stesso mese.
 */
export function righeDaFormule(
  q: QuadriInCostruzione,
  esclusi: ReadonlySet<QuadroModello>,
  consumi: readonly ConsumoMensile[],
  dati: DatiPeriodo,
  regole: RegolePeriodo,
): Diagnostica[] {
  const diagnostiche: Diagnostica[] = []
  const energia = regole.arrotondamento.energia
  const ambito = ambitoDi(dati, regole)
  const fp = dati.formulePersonalizzate

  for (const { mese } of mesiDelPeriodo(dati.periodo, dati.annoImposta).mesi) {
    const righi: Record<string, Dec> = {}
    const ctx = contestoDelMese(consumi, dati, regole, mese, righi)

    if (fp.perDifferenza !== undefined) {
      const { quadro, tipologia, formula } = fp.perDifferenza
      const kwh = arrotonda(valuta(formula, ctx), energia)
      if (kwh.isNegative()) {
        diagnostiche.push(
          bloccante('CONSUMO_PER_DIFFERENZA_NEGATIVO', `consumo per differenza negativo nel mese ${mese}`, { quadro, mese }),
        )
      } else {
        aggiungiRiga(q, esclusi, quadro, { ambito, mese, kwh, tipologia })
      }
    }

    for (const r of fp.righi ?? []) {
      const kwh = arrotonda(valuta(r.formula, ctx), energia)
      righi[`${r.quadro}.${r.progr}`] = kwh
      if (kwh.isNegative()) {
        diagnostiche.push(bloccante('RIGO_NEGATIVO', `${r.quadro}${r.progr} negativo nel mese ${mese}`, { quadro: r.quadro, mese }))
        continue
      }
      aggiungiRiga(q, esclusi, r.quadro, {
        ambito,
        mese,
        progr: r.progr,
        numeroUtenze: r.numeroUtenze,
        rett: 0,
        kwh,
        tipologia: `${r.quadro}${r.progr}`,
      })
    }

    if (fp.quadroM !== undefined && regole.formulaQuadroM !== undefined) {
      const kwh = arrotonda(valuta(regole.formulaQuadroM, ctx), energia)
      if (kwh.isNegative()) {
        diagnostiche.push(bloccante('RIGO_NEGATIVO', `quadro M negativo nel mese ${mese}`, { quadro: 'M', mese }))
        continue
      }
      aggiungiRiga(q, esclusi, 'M', {
        ambito,
        mese,
        numeroUtenze: fp.quadroM.numeroUtenze,
        rett: 0,
        kwh,
        tipologia: regole.scaglioni?.tipologieSoggette[0],
      })
    }
  }
  return diagnostiche
}
